import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import cx from 'classnames';
import axios from '~/utils/axios';
import {
  IconButton,
  Theme,
  CircularProgress,
  Typography,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import DoneIcon from '@material-ui/icons/CheckCircleTwoTone';
import WarningIcon from '@material-ui/icons/WarningTwoTone';
import InfoIcon from '@material-ui/icons/InfoTwoTone';
import FailIcon from '@material-ui/icons/HighlightOffTwoTone';
import GetAppIcon from '@material-ui/icons/GetApp';
import {
  getFileName,
  useBackgroundTasks,
  useCheckBackgroundTask,
  useCreateBackgroundTask,
  useCreateBackgroundTaskImport,
} from '~/hooks/useBackgroundTasks';
import { Button, FileField, FileInput, SimpleForm, SolutionSelect } from '~/components/ra';
import {
  BackgroundTaskStatus,
  CreateBackgroundTaskParams,
  ImportBackgroundTaskResult,
} from '~/api/backgroundTasks';
import { SaveButton, required } from 'react-admin';
import Toolbar from './ra/Toolbar';
import FileSaver from 'file-saver';
import onError from '~/errorsHandler';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

export const useBackgroundTaskTranslate = () => {
  const translate = useTranslate();
  return (key: string, args?: any): string => translate(`components.BackgroundTask.${key}`, args);
};

export type BackgroundTaskDialogType = 'import' | 'export';

interface Props {
  type: CreateBackgroundTaskParams['type'];
  dialogType: BackgroundTaskDialogType;
  dialogTitle?: string;
  children: JSX.Element;
  queryArguments?: any;
  confirmationSettings?: {
    confirmationTitle: ReactNode;
    confirmationBody: ReactNode;
  };
}

const BackgroundTaskDialog: FC<Props> = (props) => {
  const { type, queryArguments = {}, dialogType } = props;

  const [isImportStart, setIsImportStart] = useState(dialogType === 'import');

  const t = useBackgroundTaskTranslate();

  const classes = useStyles();

  const createBackgroundTaskImport = useCreateBackgroundTaskImport({
    onError: (error: any) => onError(error),
  });

  const createBackgroundTaskExport = useCreateBackgroundTask({ onError: () => null });

  const {
    currentBackgroundTasks,
    currentBackgroundTaskRemove,
    backgroundTaskResultData,
  } = useBackgroundTasks({ type, queryArguments, dialogType, isEnabled: !isImportStart });

  const mutationStatus = createBackgroundTaskExport.isError && 'FAILED';
  const mutationErrorMessgage = (createBackgroundTaskExport.error as any)?.body?.message;
  const status = mutationStatus || currentBackgroundTasks?.status;
  const statusMessage = mutationErrorMessgage || currentBackgroundTasks?.statusMessage;

  useEffect(() => {
    currentBackgroundTaskRemove();
    if (dialogType === 'export') {
      try {
        createBackgroundTaskExport.mutateAsync({
          type,
          arguments: queryArguments,
        });
      } catch (error) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (formData: any) => {
    await createBackgroundTaskImport.mutateAsync({
      type: type,
      formData: formData,
      queryArguments: queryArguments,
    });
    setIsImportStart(false);
  };
  return (
    <Grid container direction='column' alignItems='stretch' justify='center' spacing={2}>
      {isImportStart ? (
        <SimpleForm
          toolbar={
            <Toolbar>
              <SaveButton onSave={handleSave} />
            </Toolbar>
          }
          saving={createBackgroundTaskImport.isLoading}
        >
          <SolutionSelect
            validate={required()}
            label={t('solutionId')}
            source='solution'
            allowEmpty={false}
            className={classes.input}
            variant='individual'
          />
          <FileInput
            accept='text/csv'
            label={t('file')}
            validate={required()}
            source='file'
            className={classes.fileInput}
          >
            <FileField ellipsis={false} />
          </FileInput>
        </SimpleForm>
      ) : (
        <ProgressContent
          status={status}
          statusMessage={statusMessage}
          dialogType={dialogType}
          backgroundTaskResultData={backgroundTaskResultData}
          type={type}
          queryArguments={queryArguments}
        />
      )}
    </Grid>
  );
};

const BackgroundTask: FC<Props> = (props) => {
  const { children, dialogTitle, confirmationSettings } = props;
  const [isConfirmed, setIsConfirmed] = useState(!confirmationSettings);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setTimeout(() => setIsConfirmed(!confirmationSettings), 500);
  };

  const handleExportClick = () => {
    handleOpen();
  };

  const classes = useStyles();

  const translate = useTranslate();

  if (!isConfirmed) {
    return (
      <>
        {React.cloneElement(children, { onClick: handleExportClick })}
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby='demo-alert-dialog-title'
          aria-describedby='demo-alert-dialog-description'
        >
          <DialogTitle id='demo-alert-dialog-title'>{dialogTitle}</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Grid container direction='column' alignItems='center' justify='center' spacing={2}>
              <div className={classes.root}>
                <InfoIcon className={cx(classes.icon, classes.WARNING)} />
                <Typography variant='h5' className={classes.text}>
                  {confirmationSettings?.confirmationTitle}
                </Typography>
                <Typography variant='subtitle2' align='center'>
                  {confirmationSettings?.confirmationBody}
                </Typography>
              </div>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button label='ra.action.cancel' onClick={handleClose} />
            <Button
              label={translate(`utils.confirmation.confirm`)}
              onClick={() => setIsConfirmed(true)}
              variant='contained'
            >
              <CheckCircleIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      {React.cloneElement(children, { onClick: handleExportClick })}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='demo-alert-dialog-title'
        aria-describedby='demo-alert-dialog-description'
      >
        <DialogTitle id='demo-alert-dialog-title'>{dialogTitle}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <BackgroundTaskDialog {...props} />
        </DialogContent>
        <DialogActions>
          <Button label='ra.action.close' onClick={handleClose} />
        </DialogActions>
      </Dialog>
    </>
  );
};

interface ProgressContentProps {
  status?: BackgroundTaskStatus;
  statusMessage?: string;
  dialogType: BackgroundTaskDialogType;
  backgroundTaskResultData?: ImportBackgroundTaskResult;
  type: CreateBackgroundTaskParams['type'];
  queryArguments?: any;
}

const ProgressContent: FC<ProgressContentProps> = (props) => {
  const {
    status,
    statusMessage,
    dialogType,
    backgroundTaskResultData,
    type,
    queryArguments,
  } = props;
  const classes = useStyles();
  const t = useBackgroundTaskTranslate();
  const { data } = useCheckBackgroundTask(type, { refetchOnMount: false });

  const download = () => {
    if (backgroundTaskResultData && backgroundTaskResultData.resultFile) {
      const fileName = getFileName({ type });
      axios
        .get(`/files/${backgroundTaskResultData.resultFile}`, { responseType: 'blob' })
        .then((response) => {
          FileSaver.saveAs(response.data, fileName);
        })
        .catch(() => null);
    }
  };
  const isExportTerminatedWithLimit = statusMessage?.includes(WITH_LIMIT_SUBSTRING);
  const exportFormat = queryArguments?.exportFormat;
  const rowsLimit =
    exportFormat === 'CSV'
      ? data?.parameters?.rowsLimitCsv
      : exportFormat === 'XLSX'
      ? data?.parameters?.rowsLimitExcel
      : undefined;
  const rowsLimitStr = rowsLimit ?? 'some';

  // todo rewrite this mess
  // I guess it would be better if there was a status condition, like: if status == DONE then <component /> else if status == FAILED then <component /> ...
  return (
    <div className={classes.root}>
      {status === 'DONE' && isExportTerminatedWithLimit ? (
        <WarningIcon className={cx(classes.icon, classes.WARNING)} />
      ) : status === 'DONE' ? (
        <DoneIcon className={cx(classes.icon, classes.DONE)} />
      ) : status === 'FAILED' ? (
        <FailIcon className={cx(classes.icon, classes.FAILED)} />
      ) : (
        <div className={classes.loadingContainer}>
          <CircularProgress color='primary' />
        </div>
      )}
      <Typography variant='h5' className={classes.text}>
        {status === 'DONE' && dialogType === 'export' && isExportTerminatedWithLimit
          ? t(`${status}_export_limited`, { maxRecordsNumber: rowsLimitStr })
          : status === 'DONE' && dialogType === 'export'
          ? t(`${status}_export`)
          : status === 'DONE' && dialogType === 'import'
          ? t(`${status}_import`)
          : status
          ? t(status)
          : t('loading')}
      </Typography>
      {status === 'DONE' ? (
        dialogType === 'import' ? (
          <>
            {backgroundTaskResultData && (
              <Typography variant='subtitle2' align='center'>
                {t('recordsAndErrors', {
                  records: backgroundTaskResultData.records,
                  errors: backgroundTaskResultData.errors,
                })}
              </Typography>
            )}
            <Typography variant='subtitle2' align='center'>
              {t('downloadTheResult')}
            </Typography>
            <IconButton id='downloadButton' onClick={download} disabled={!backgroundTaskResultData}>
              <GetAppIcon />
            </IconButton>
          </>
        ) : dialogType === 'export' && isExportTerminatedWithLimit ? (
          <Typography variant='subtitle2' align='center'>
            {t('downloadWillStartLimited', { maxRecordsNumber: rowsLimitStr })}
          </Typography>
        ) : (
          <Typography variant='subtitle2' align='center'>
            {t('downloadWillStart')}
          </Typography>
        )
      ) : (
        <Typography variant='subtitle2' align='center'>
          {statusMessage}
        </Typography>
      )}
    </div>
  );
};

const WITH_LIMIT_SUBSTRING = 'export terminated';

const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    width: 400,
    minHeight: 120,
  },
  input: {
    width: 'unset',
  },
  fileInput: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    '& .previews': {
      '& > div > button': {
        display: 'none',
      },
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  icon: {
    fontSize: '64px',
  },
  loadingContainer: {
    width: 64,
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1) / 2,
  },
  DONE: {
    color: theme.palette.success.main,
  },
  FAILED: {
    color: theme.palette.error.main,
  },
  WARNING: {
    color: theme.palette.warning.main,
  },
}));

export default BackgroundTask;
